import { NumberField } from "@/components/generic/NumberField";
import { MaybePromise } from "@/types/maybe-promise";
import { StepperSubmitHandler } from "@/utils/stepper";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import z from "zod";
import { SegmentHeader } from "../../basic/SegmentHeader";
import {
  CampaignPeriodField,
  campaignPeriodFieldValueSchema,
} from "./CampaignPeriodField";

export const maxLeadsPerWeekAndPeriodFormSchema = z.object({
  maxLeadsPerWeek: z
    .number()
    .min(1, { message: "Maksimal antal leads pr. uge er påkrævet" })
    .nullable(),
  period: campaignPeriodFieldValueSchema,
});

export type MaxLeadsPerWeekAndPeriodFormValues = z.infer<
  typeof maxLeadsPerWeekAndPeriodFormSchema
>;

export function MaxLeadsPerWeekAndPeriodForm({
  defaultValues,
  values,
  onSubmit,
  setSubmitHandler,
  autoSubmit,
}: {
  defaultValues: MaxLeadsPerWeekAndPeriodFormValues;
  values?: MaxLeadsPerWeekAndPeriodFormValues;
  onSubmit: (values: MaxLeadsPerWeekAndPeriodFormValues) => MaybePromise<void>;
  setSubmitHandler?: (submitHandler: StepperSubmitHandler) => void;
  autoSubmit?: boolean;
}) {
  const {
    control,
    handleSubmit,
    formState: {
      isDirty,
      isValid,
      isSubmitted,
      isSubmitting,
      defaultValues: formCurrentDefaultValues,
    },
    setValue,
    reset,
    trigger,
  } = useForm({
    resolver: zodResolver(maxLeadsPerWeekAndPeriodFormSchema),
    defaultValues,
  });

  useEffect(() => {
    if (defaultValues === formCurrentDefaultValues) return;
    reset(defaultValues, { keepValues: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValues]);

  useEffect(() => {
    if (!values) return;
    setValue("maxLeadsPerWeek", values.maxLeadsPerWeek);
    setValue("period", values.period);
  }, [setValue, values]);

  const submit = useMemo(
    () => handleSubmit(onSubmit),
    [handleSubmit, onSubmit],
  );

  useEffect(() => {
    setSubmitHandler?.({
      submit,
      isDisabled: (!isValid && isSubmitted) || isSubmitting,
      isDirty,
      isPending: isSubmitting,
    });
  }, [setSubmitHandler, submit, isValid, isDirty, isSubmitted, isSubmitting]);

  const autoSubmittedRef = useRef(false);

  useEffect(() => {
    if (autoSubmittedRef.current) return;

    if (autoSubmit) {
      autoSubmittedRef.current = true;
      submit();
    }
  }, [autoSubmit, submit]);

  return (
    <form className="flex flex-col items-center gap-y-10" onSubmit={submit}>
      <SegmentHeader
        title="Max leads per uge"
        description="Indtast det antal leads, du højest ønsker at modtage per uge."
      />

      <NumberField
        className="w-full max-w-80"
        control={control}
        name="maxLeadsPerWeek"
        aria-label="Max leads per uge"
        placeholder="Max leads per uge"
      />

      <CampaignPeriodField control={control} name="period" trigger={trigger} />
    </form>
  );
}
